import React, { useEffect, useState } from "react";
import { auth, provider, db } from "../../firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { deleteAdminMedia, uploadAdminMedia } from "../../lib/mediaUpload";
import { useToast } from "../../context/ToastContext";

const ADMIN_EMAILS = ["yero.musique@gmail.com", "unlimitedsession@gmail.com"];
const DEFAULT_DATA = {
  pageStatus: "active",
  unavailableMessage: "Cette page artiste est momentanément indisponible.",
  slide1: { image: "", titleTop: "", subtitleTop: "" },
  slide2: { video: "", caption: "" },
  slide3: { heading: "", text: "" },
  slide4: { image: "", caption: "", titleTop: "" },
  gallery: [],
  ymsSection: { title: "", text: "" },
  covers: [],
  coversLinks: [],
  coversTitle: "Découvrez nos pochettes",
};

export default function AdminArtistes() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [data, setData] = useState(DEFAULT_DATA);
  const [uploading, setUploading] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [videoUrlDraft, setVideoUrlDraft] = useState("");
  const toast = useToast();

  // ===== Auth =====
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && ADMIN_EMAILS.includes(currentUser.email)) {
        setUser(currentUser);
        setError("");
        await fetchData();
      } else if (currentUser) {
        setError("Accès refusé");
        signOut(auth);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setVideoUrlDraft(data.slide2.video || "");
  }, [data.slide2.video]);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch {
      setError("Connexion annulée ou échouée.");
    }
  };
  const handleLogout = () => signOut(auth);

  const uploadMedia = async (file, folder, label = "media") => {
    if (!file) return "";
    setUploading(label);
    setUploadProgress(0);
    setUploadError("");
    try {
      return await uploadAdminMedia(file, folder, {
        onProgress: setUploadProgress,
      });
    } catch (err) {
      console.error("Upload admin artistes impossible", err);
      const message = err?.message || "Erreur inconnue pendant l'upload.";
      setUploadError(message);
      toast.error(
        "Upload impossible",
        message.length > 140 ? `${message.slice(0, 140)}...` : message
      );
      return "";
    } finally {
      setUploading("");
      setUploadProgress(0);
    }
  };

  // ===== Firestore =====
  const fetchData = async () => {
    const docRef = doc(db, "admin", "artistes");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const d = snap.data();
      setData({
        ...DEFAULT_DATA,
        ...d,
        slide1: { ...DEFAULT_DATA.slide1, ...(d.slide1 || {}) },
        slide2: { ...DEFAULT_DATA.slide2, ...(d.slide2 || {}) },
        slide3: { ...DEFAULT_DATA.slide3, ...(d.slide3 || {}) },
        slide4: { ...DEFAULT_DATA.slide4, ...(d.slide4 || {}) },
        ymsSection: { ...DEFAULT_DATA.ymsSection, ...(d.ymsSection || {}) },
        gallery: d.gallery || [],
        covers: d.covers || [],
        coversLinks: d.coversLinks || [],
      });
    }
  };

  const setField = async (path, value, successMessage = "Modification enregistrée") => {
    const levels = path.split(".");
    const updated = { ...data };
    let target = updated;
    for (let i = 0; i < levels.length - 1; i++) {
      if (!target[levels[i]]) target[levels[i]] = {};
      target = target[levels[i]];
    }
    target[levels[levels.length - 1]] = value;
    setData(updated);
    await saveData(updated, successMessage);
  };

  const saveData = async (updated, successMessage) => {
    const docRef = doc(db, "admin", "artistes");
    try {
      await setDoc(docRef, sanitizeForFirestore(updated), { merge: true });
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      console.error("Sauvegarde artistes impossible", err);
      toast.error("Sauvegarde impossible", "La modification n'a pas ete enregistree.");
    }
  };

  const addGalleryImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadMedia(file, "artistes");
    e.target.value = "";
    if (!url) return;
    const updated = { ...data, gallery: [...(data.gallery || []), url] };
    setData(updated);
    await saveData(updated, "Image ajoutée");
  };

  const removeGalleryImage = async (index) => {
    const updatedGallery = data.gallery.filter((_, i) => i !== index);
    const updated = { ...data, gallery: updatedGallery };
    setData(updated);
    await saveData(updated, "Image supprimée");
  };

  // ===== Covers =====
  const addCover = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadMedia(file, "artistes_covers");
    e.target.value = "";
    if (!url) return;
    const updated = { ...data, covers: [...(data.covers || []), url] };
    setData(updated);
    await saveData(updated, "Cover ajoutée");
  };

  const removeCover = async (index) => {
    const updatedCovers = data.covers.filter((_, i) => i !== index);
    const updated = { ...data, covers: updatedCovers };
    setData(updated);
    await saveData(updated, "Cover supprimée");
  };

  const uploadSlide2Video = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!String(file.type || "").startsWith("video/")) {
      toast.error("Fichier invalide", "Choisis un fichier video.");
      return;
    }

    const url = await uploadMedia(file, "artistes/videos", "slide2Video");
    if (!url) return;
    setVideoUrlDraft(url);
    await setField("slide2.video", url, "Vidéo mise à jour");
  };

  const saveSlide2VideoUrl = async () => {
    const value = videoUrlDraft.trim();
    if (value && !/^(https?:\/\/|\/)/i.test(value)) {
      toast.error("URL invalide", "Colle une URL video complete ou un chemin commencant par /.");
      return;
    }

    await setField(
      "slide2.video",
      value,
      value ? "URL vidéo enregistrée" : "Vidéo supprimée"
    );
  };

  const removeSlide2Video = async () => {
    const previousUrl = data.slide2.video;
    if (!previousUrl) return;

    const updated = {
      ...data,
      slide2: { ...data.slide2, video: "" },
    };
    setVideoUrlDraft("");
    setData(updated);
    await saveData(updated, "Vidéo supprimée");
    deleteAdminMedia(previousUrl);
  };

  // ===== UI =====
  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col justify-center items-center">
        <h1 className="text-2xl font-bold mb-4">Connexion Admin Artistes</h1>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <button
          onClick={handleLogin}
          className="bg-white text-black px-6 py-2 rounded hover:bg-gray-200 transition"
        >
          Connexion avec Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8 space-y-12">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center">
          <img src={user.photoURL} alt="profil" className="w-10 h-10 rounded-full" />
          <div>
            <p className="font-bold">{user.displayName}</p>
            <p className="text-sm text-gray-400">{user.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
        >
          Se déconnecter
        </button>
      </div>

      {/* Slide 1 */}
      <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Visibilité artiste</p>
        <h2 className="mt-2 text-2xl font-black uppercase">Page YMS</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">Statut</span>
            <select
              value={data.pageStatus || "active"}
              onChange={(event) => setField("pageStatus", event.target.value, "Statut artiste mis à jour")}
              className="w-full rounded border border-white/10 bg-black px-3 py-2 text-white outline-none focus:border-red-500"
            >
              <option value="active">Actif</option>
              <option value="hidden">Masqué</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">Message visiteur</span>
            <input
              type="text"
              value={data.unavailableMessage || ""}
              onChange={(event) => setData((current) => ({ ...current, unavailableMessage: event.target.value }))}
              onBlur={(event) => setField("unavailableMessage", event.target.value, "Message artiste enregistré")}
              className="w-full rounded border border-white/10 bg-black px-3 py-2 text-white outline-none focus:border-red-500"
            />
          </label>
        </div>
      </section>

      <h2 className="text-3xl font-bold">Slide 1 (Image + Texte)</h2>
      <input
        type="file"
        accept="image/*"
        onChange={async (e) => {
          const url = await uploadMedia(e.target.files[0], "artistes");
          e.target.value = "";
          if (url) await setField("slide1.image", url);
        }}
      />
      {data.slide1.image && <img src={data.slide1.image} alt="" className="w-64 rounded mt-2" />}
      <input
        type="text"
        placeholder="Titre haut"
        value={data.slide1.titleTop}
        onChange={(e) =>
          setData((d) => ({ ...d, slide1: { ...d.slide1, titleTop: e.target.value } }))
        }
        onBlur={(e) => setField("slide1.titleTop", e.target.value)}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />
      <input
        type="text"
        placeholder="Sous-titre"
        value={data.slide1.subtitleTop}
        onChange={(e) =>
          setData((d) => ({ ...d, slide1: { ...d.slide1, subtitleTop: e.target.value } }))
        }
        onBlur={(e) => setField("slide1.subtitleTop", e.target.value)}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />

      {/* Slide 2 */}
      <h2 className="text-3xl font-bold">Slide 2 (Vidéo)</h2>
      <div className="rounded-lg border border-white/10 bg-zinc-950 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <label
            className={`inline-flex w-fit cursor-pointer rounded bg-white px-4 py-2 text-sm font-bold text-black transition hover:bg-gray-200 ${
              uploading ? "pointer-events-none opacity-60" : ""
            }`}
          >
            {data.slide2.video ? "Charger une nouvelle vidéo" : "Ajouter une vidéo"}
            <input
              type="file"
              accept="video/*"
              onChange={uploadSlide2Video}
              className="hidden"
              disabled={Boolean(uploading)}
            />
          </label>
          {data.slide2.video && (
            <button
              onClick={removeSlide2Video}
              disabled={Boolean(uploading)}
              className="w-fit rounded bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Supprimer la vidéo
            </button>
          )}
          {data.slide2.video && (
            <a
              href={data.slide2.video}
              target="_blank"
              rel="noreferrer"
              className="w-fit rounded border border-white/15 px-4 py-2 text-sm font-bold text-white transition hover:border-white"
            >
              Ouvrir le fichier
            </a>
          )}
        </div>

        {uploading === "slide2Video" && (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between text-sm text-gray-300">
              <span>Upload vidéo en cours...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-red-600 transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {uploadError && (
          <div className="mt-4 rounded border border-red-500/30 bg-red-950/30 p-3 text-sm text-red-100">
            <p className="font-bold">Dernière erreur upload</p>
            <p className="mt-1 break-words text-red-100/80">{uploadError}</p>
          </div>
        )}

        <div className="mt-4 rounded border border-white/10 bg-black p-3">
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">
            URL vidéo
          </label>
          <div className="flex flex-col gap-2 md:flex-row">
            <input
              type="text"
              value={videoUrlDraft}
              onChange={(e) => setVideoUrlDraft(e.target.value)}
              placeholder="https://.../video.mp4 ou /videos/video.mp4"
              className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white outline-none focus:border-red-500"
            />
            <button
              onClick={saveSlide2VideoUrl}
              disabled={Boolean(uploading)}
              className="rounded bg-white px-4 py-2 text-sm font-bold text-black transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Enregistrer l'URL
            </button>
            <button
              onClick={() => {
                setVideoUrlDraft("");
                setField("slide2.video", "", "Vidéo supprimée");
              }}
              disabled={Boolean(uploading)}
              className="rounded border border-white/15 px-4 py-2 text-sm font-bold text-white transition hover:border-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              Vider
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Si l'upload bloque, colle ici une URL Firebase, Cloudinary ou un chemin public.
          </p>
        </div>

        {data.slide2.video ? (
          <video
            key={data.slide2.video}
            src={data.slide2.video}
            controls
            playsInline
            preload="metadata"
            onError={() =>
              toast.error("Vidéo illisible", "Le fichier actuel ne peut pas etre lu par le navigateur.")
            }
            className="mt-4 aspect-video w-full max-w-lg rounded bg-black object-cover"
          />
        ) : (
          <div className="mt-4 grid aspect-video w-full max-w-lg place-items-center rounded border border-dashed border-white/15 bg-black text-sm text-gray-500">
            Aucune vidéo pour cette slide
          </div>
        )}
      </div>
      <input
        type="text"
        placeholder="Texte sous vidéo"
        value={data.slide2.caption}
        onChange={(e) =>
          setData((d) => ({ ...d, slide2: { ...d.slide2, caption: e.target.value } }))
        }
        onBlur={(e) => setField("slide2.caption", e.target.value, "Texte vidéo enregistré")}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />

      {/* Slide 3 */}
      <h2 className="text-3xl font-bold">Slide 3 (Biographie)</h2>
      <input
        type="text"
        placeholder="Titre"
        value={data.slide3.heading}
        onChange={(e) =>
          setData((d) => ({ ...d, slide3: { ...d.slide3, heading: e.target.value } }))
        }
        onBlur={(e) => setField("slide3.heading", e.target.value)}
        className="w-full p-2 rounded bg-zinc-800 border border-zinc-700"
      />
      <textarea
        placeholder="Texte"
        value={data.slide3.text}
        onChange={(e) =>
          setData((d) => ({ ...d, slide3: { ...d.slide3, text: e.target.value } }))
        }
        onBlur={(e) => setField("slide3.text", e.target.value)}
        className="w-full h-40 p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />

      {/* Slide 4 */}
      <h2 className="text-3xl font-bold">Slide 4 (Image + Texte)</h2>
      <input
        type="file"
        accept="image/*"
        onChange={async (e) => {
          const url = await uploadMedia(e.target.files[0], "artistes");
          e.target.value = "";
          if (url) await setField("slide4.image", url);
        }}
      />
      {data.slide4.image && <img src={data.slide4.image} alt="" className="w-64 rounded mt-2" />}
      <input
        type="text"
        placeholder="Titre haut"
        value={data.slide4.titleTop}
        onChange={(e) =>
          setData((d) => ({ ...d, slide4: { ...d.slide4, titleTop: e.target.value } }))
        }
        onBlur={(e) => setField("slide4.titleTop", e.target.value)}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />
      <input
        type="text"
        placeholder="Texte bas"
        value={data.slide4.caption}
        onChange={(e) =>
          setData((d) => ({ ...d, slide4: { ...d.slide4, caption: e.target.value } }))
        }
        onBlur={(e) => setField("slide4.caption", e.target.value)}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />

      {/* Galerie */}
      <h2 className="text-3xl font-bold">Galerie</h2>
      <input type="file" accept="image/*" onChange={addGalleryImage} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        {data.gallery.map((img, i) => (
          <div key={i} className="relative">
            <img src={img} alt={`gallery-${i}`} className="rounded-lg w-full h-32 object-cover" />
            <button
              onClick={() => removeGalleryImage(i)}
              className="absolute top-2 right-2 bg-red-600 px-2 py-1 text-xs rounded"
            >
              Supprimer
            </button>
          </div>
        ))}
      </div>

      {/* Covers */}
<h2 className="text-3xl font-bold">Section Covers</h2>

<input
  type="text"
  placeholder="Titre de la section"
  value={data.coversTitle}
  onChange={(e) => setData((d) => ({ ...d, coversTitle: e.target.value }))}
  onBlur={(e) => setField("coversTitle", e.target.value)}
  className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
/>

<label className="mt-4 inline-block cursor-pointer rounded bg-white px-4 py-2 text-sm font-bold text-black hover:bg-gray-200 transition">
  Ajouter une cover
  <input type="file" accept="image/*" onChange={addCover} className="hidden" />
</label>

<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
  {data.covers.map((cover, i) => (
    <div key={i} className="bg-zinc-900 rounded-lg p-4 flex flex-col gap-2">
      {cover ? (
        <img
          src={cover}
          alt={`Cover ${i + 1}`}
          className="w-full h-40 object-cover rounded"
        />
      ) : (
        <div className="w-full h-40 bg-gray-800 rounded flex items-center justify-center text-gray-500">
          Cover {i + 1}
        </div>
      )}
      <label className="cursor-pointer bg-white text-black px-3 py-1 rounded text-sm hover:bg-gray-200 transition">
        {cover ? "Changer l’image" : "Ajouter une image"}
        <input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const url = await uploadMedia(file, "covers");
            e.target.value = "";
            if (!url) return;
            const updated = [...(data.covers || [])];
            updated[i] = url;
            await setField("covers", updated);
          }}
          className="hidden"
/>
      </label>
      <input
        type="text"
        placeholder="Lien Spotify open.spotify.com, YouTube ou page interne"
        value={data.coversLinks?.[i] || ""}
        onChange={(e) => {
          const updated = [...(data.coversLinks || [])];
          updated[i] = e.target.value;
          setData((d) => ({ ...d, coversLinks: updated }));
        }}
        onBlur={(e) => setField(`coversLinks.${i}`, e.target.value)}
        className="w-full p-1 rounded bg-zinc-800 border border-gray-700 text-white text-sm"
/>
      <p className="text-xs text-gray-500">
        Les liens Spotify s'ouvrent dans un player intégré au site.
      </p>
      <button
        onClick={() => removeCover(i)}
        className="rounded bg-red-600 px-3 py-1 text-xs font-bold uppercase"
      >
        Supprimer
      </button>
    </div>
  ))}
</div>
      {/* YMS Section */}
      <h2 className="text-3xl font-bold">Section YMS (Texte)</h2>
      <input
        type="text"
        placeholder="Titre"
        value={data.ymsSection?.title || ""}
        onChange={(e) =>
          setData((d) => ({ ...d, ymsSection: { ...d.ymsSection, title: e.target.value } }))
        }
        onBlur={(e) => setField("ymsSection.title", e.target.value)}
        className="w-full p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />
      <textarea
        placeholder="Texte"
        value={data.ymsSection?.text || ""}
        onChange={(e) =>
          setData((d) => ({ ...d, ymsSection: { ...d.ymsSection, text: e.target.value } }))
        }
        onBlur={(e) => setField("ymsSection.text", e.target.value)}
        className="w-full h-40 p-2 mt-2 rounded bg-zinc-800 border border-zinc-700"
      />
    </div>
  );
}
